import Alert from '@mui/material/Alert';
import Typography from '@mui/material/Typography';
import { AutoComplete } from '@openx/components-core/lib/AutoComplete/AutoComplete.tsx';
import { FormSection } from '@openx/components-core/lib/FormSection/FormSection.tsx';
import { RIGHT_COLUMN_WIDTH } from '@openx/constants/form.ts';
import { InventoryListFormFields, type InventoryListFormState } from '@openx/types/inventoryList.ts';
import { useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { getFormLabels, getInventoryInfoText, listTypes, listTypesLabels } from '../../../constants.tsx';
import { InventoryListsBulkAdd } from './InventoryListsBulkAdd/InventoryListsBulkAdd.tsx';

type InventorySectionProps = {
  isEdit: boolean;
};

export const InventorySection = ({ isEdit }: InventorySectionProps) => {
  const { getValues, setValue } = useFormContext<InventoryListFormState>();
  const { t } = useTranslation();
  const formLabels = useMemo(() => getFormLabels(), []);
  const listDropdownLabels = useMemo(() => listTypesLabels(), []);
  const [selectedType, setSelectedType] = useState(getValues(InventoryListFormFields.LIST_TYPE));

  return (
    <FormSection
      title={t('Add Inventory List Items')}
      description={t(
        'Adding items to Inventory List will enable their automatic application in all Deals within the Organization.'
      )}
      rightColumnSize={RIGHT_COLUMN_WIDTH}
    >
      <AutoComplete
        options={listTypes}
        getOptionLabel={option => listDropdownLabels[option]}
        textFieldProps={{
          label: formLabels[InventoryListFormFields.LIST_TYPE],
        }}
        data-test={'data-type'}
        disableClearable
        disabled={isEdit}
        onChange={(_, value) => {
          setValue(InventoryListFormFields.LIST_TYPE, value);
          setSelectedType(value);
        }}
        value={selectedType}
      />

      <Typography variant="body1Bold" sx={{ mt: 1 }}>
        {t('{type} [ {count} ]', {
          type: listDropdownLabels[selectedType],
          count: getValues(InventoryListFormFields.VALUES)?.length || 0,
        })}
      </Typography>

      <Alert severity="info" sx={{ mb: 2, mt: 1 }}>
        {getInventoryInfoText(selectedType)}
      </Alert>

      <InventoryListsBulkAdd type={selectedType} />
    </FormSection>
  );
};
